import React, { useState } from 'react';
import ReportUploader from '../components/reports/ReportUploader';
import ReportViewer from '../components/reports/ReportViewer';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const ReportsManagementPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'upload' | 'view'>('view');
  const [viewPath, setViewPath] = useState('Africa/Nigeria/Lagos');
  const [refreshKey, setRefreshKey] = useState(0);

  // Save the current location before redirecting to sign in
  const handleSignInRedirect = () => {
    // Store the intended destination
    sessionStorage.setItem('redirectAfterLogin', '/reports-management');
    navigate('/signin');
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Authentication Required
          </h2>
          <p className="mt-2 text-gray-600">
            Please sign in to access security reports
          </p>
          <button
            onClick={handleSignInRedirect}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const handleUploadSuccess = () => {
    // Refresh the viewer after successful upload
    setRefreshKey((prev) => prev + 1);
    setActiveTab('view');
  };

  const commonLocations = [
    { label: 'Lagos, Nigeria', path: 'Africa/Nigeria/Lagos' },
    { label: 'Abuja, Nigeria', path: 'Africa/Nigeria/Abuja' },
    { label: 'Accra, Ghana', path: 'Africa/Ghana/Accra' },
    { label: 'Ontario, Canada', path: 'North America/Canada/Ontario' },
    { label: 'Manitoba, Canada', path: 'North America/Canada/Manitoba' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Security Reports Management
          </h1>
          <p className="mt-2 text-gray-600">
            Upload and access security assessment reports
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('view')}
                className={`${
                  activeTab === 'view'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                <svg
                  className="h-5 w-5 inline-block mr-2 -mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                View Reports
              </button>
              <button
                onClick={() => setActiveTab('upload')}
                className={`${
                  activeTab === 'upload'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                <svg
                  className="h-5 w-5 inline-block mr-2 -mt-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                Upload Report
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {activeTab === 'view' ? (
              <div className="space-y-4">
                {/* Location Selector */}
                <div className="bg-white rounded-lg shadow p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Location
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={viewPath}
                      onChange={(e) => setViewPath(e.target.value)}
                      placeholder="Enter path (e.g., Africa/Nigeria/Lagos)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <button
                      onClick={() => setRefreshKey((prev) => prev + 1)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
                    >
                      Load
                    </button>
                  </div>
                </div>

                {/* Report Viewer */}
                <ReportViewer
                  key={refreshKey}
                  folderPath={viewPath}
                  title={`Reports for ${viewPath}`}
                />
              </div>
            ) : (
              <ReportUploader
                defaultPath="Africa/Nigeria/Lagos"
                onUploadSuccess={handleUploadSuccess}
              />
            )}
          </div>

          {/* Sidebar - Quick Links */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Quick Access
              </h3>
              <div className="space-y-2">
                {commonLocations.map((location) => (
                  <button
                    key={location.path}
                    onClick={() => {
                      setViewPath(location.path);
                      setActiveTab('view');
                      setRefreshKey((prev) => prev + 1);
                    }}
                    className="w-full text-left px-4 py-2 text-sm rounded-md bg-gray-50 hover:bg-gray-100 text-gray-700 transition-colors"
                  >
                    <svg
                      className="h-4 w-4 inline-block mr-2 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {location.label}
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Storage Structure
                </h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>📁 Africa</p>
                  <p className="pl-4">📁 Nigeria</p>
                  <p className="pl-8">📁 Lagos</p>
                  <p className="pl-8">📁 Abuja</p>
                  <p className="pl-4">📁 Ghana</p>
                  <p className="pl-8">📁 Accra</p>
                  <p>📁 North America</p>
                  <p className="pl-4">📁 Canada</p>
                  <p className="pl-8">📁 Ontario</p>
                  <p className="pl-8">📁 Manitoba</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsManagementPage;
